import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getDirectDocumentBudget } from "@/api/chat/chat";

import {
    evaluateNewCountedDocuments,
    evaluateSelectedDocuments,
    type DirectDocumentBudgetEvaluation,
    type DirectDocumentBudgetStatus,
} from "./direct-document-budget.utils";

export function directDocumentBudgetQueryKey(
    conversationId: number | string | undefined
) {
    return ["chat", "direct-document-budget", conversationId] as const;
}

export interface UseDirectDocumentBudgetOptions {
    /** Undefined until the conversation exists; the composer opens before it does. */
    conversationId?: number | string;
    /** Org-tunable cap. Undefined means the org context has not resolved. */
    limit: number | undefined;
    /**
     * Counted document ids staged in the composer but not yet persisted.
     *
     * An input rather than internal state on purpose: the composer already
     * holds this set, and a second copy here could disagree with it. The owner
     * derives it from live composer state and passes it down, so attaching
     * needs no notification call.
     */
    stagedIds?: readonly number[];
    /** Gate the fetch on a picker being open. */
    enabled?: boolean;
}

export interface DirectDocumentBudget {
    status: DirectDocumentBudgetStatus;
    limit: number | undefined;
    /** Complete eligible set from the server. */
    eligibleIds: number[];
    stagedIds: number[];
    /** Documents already drawing on the budget, including staged ones. */
    heldCount: number;
    isLegacyOverCap: boolean;
    /** Dedup-aware path for pickers that yield real document ids. */
    evaluate: (
        selectedIds: readonly number[]
    ) => DirectDocumentBudgetEvaluation;
    /** Count-based path for uploads, whose documents are structurally new. */
    evaluateNewCount: (newCount: number) => DirectDocumentBudgetEvaluation;
    retry: () => void;
    isRetrying: boolean;
    /** Drop the cached budget after attachments persist. */
    invalidate: () => void;
}

/**
 * Owns the budget query, the composer-staged set, and the set arithmetic.
 *
 * Both evaluation paths are returned rather than one merged path: see
 * `direct-document-budget.utils.ts` for why collapsing them is unsafe.
 */
export function useDirectDocumentBudget({
    conversationId,
    limit,
    stagedIds: stagedIdsInput,
    enabled = true,
}: UseDirectDocumentBudgetOptions): DirectDocumentBudget {
    const queryClient = useQueryClient();
    const stagedIds = useMemo(
        () => [...(stagedIdsInput ?? [])],
        [stagedIdsInput]
    );

    const query = useQuery({
        queryKey: directDocumentBudgetQueryKey(conversationId),
        queryFn: () => getDirectDocumentBudget(conversationId!),
        enabled: conversationId != null && enabled,
    });

    const eligibleIds = useMemo(
        () => query.data?.eligible_document_ids ?? [],
        [query.data]
    );

    const status: DirectDocumentBudgetStatus =
        conversationId == null
            ? "new-thread"
            : query.isError
              ? "error"
              : limit == null || query.isPending
                ? "pending"
                : "ready";

    const heldCount = useMemo(
        () => new Set([...eligibleIds, ...stagedIds]).size,
        [eligibleIds, stagedIds]
    );

    // The server is the authority on the limit; a missing one is a pending
    // state, never a locally invented cap.
    const effectiveLimit = limit ?? 0;

    const evaluate = useCallback(
        (selectedIds: readonly number[]) =>
            evaluateSelectedDocuments({
                eligibleIds,
                stagedIds,
                selectedIds,
                limit: effectiveLimit,
            }),
        [eligibleIds, stagedIds, effectiveLimit]
    );

    const evaluateNewCount = useCallback(
        (newCount: number) =>
            evaluateNewCountedDocuments(newCount, heldCount, effectiveLimit),
        [heldCount, effectiveLimit]
    );

    const invalidate = useCallback(() => {
        void queryClient.invalidateQueries({
            queryKey: directDocumentBudgetQueryKey(conversationId),
        });
    }, [queryClient, conversationId]);

    const retry = useCallback(() => {
        void query.refetch();
    }, [query]);

    return {
        status,
        limit,
        eligibleIds,
        stagedIds,
        heldCount,
        isLegacyOverCap: limit != null && heldCount > limit,
        evaluate,
        evaluateNewCount,
        retry,
        isRetrying: query.isRefetching,
        invalidate,
    };
}
